import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, CircleStop } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import StatusMessage from "@/components/ui/status-message";
import useRealTime from "@/hooks/useRealtime";
import useAudioRecorder from "@/hooks/useAudioRecorder";
import useAudioPlayer from "@/hooks/useAudioPlayer";
import { ConversationMessage, ConversationModal } from "@/components/conversation-modal";
import { ToolResult } from "./types";
// import Toast from "@/components/ui/Toast";

function App() {
    const [isRecording, setIsRecording] = useState(false);
    const [conversation, setConversation] = useState<ConversationMessage[]>([]);
    const [conversationFinished, setConversationFinished] = useState(false);

    // At the top of App:
    const [isInturpted, setIsInturpted] = useState(true);
    const [isMicMuted, setIsMicMuted] = useState(false);
    const isMicMutedRef = useRef(isMicMuted);
    useEffect(() => {
        isMicMutedRef.current = isMicMuted;
    }, [isMicMuted]);

    const { startSession, addUserAudio, inputAudioBufferClear, closeSession } = useRealTime({
        onWebSocketOpen: () => console.log("WebSocket connection opened"),
        onWebSocketClose: () => console.log("WebSocket connection closed"),
        onWebSocketError: event => console.error("WebSocket error:", event),
        onReceivedError: message => console.error("error", message),

        onReceivedResponseAudioDelta: message => {
            if (isInturpted) {
                stopAudioPlayer();
                setIsMicMuted(false); // Mute mic when audio starts

                return;
            }
            setIsMicMuted(true); // Mute mic when audio starts

            if (isRecording) {
                playAudio(message.delta, () => {
                    setIsMicMuted(false);
                    setIsInturpted(false);
                });
            }
        },
        onReceivedExtensionMiddleTierToolResponse: msg => {
            const result: ToolResult = JSON.parse(msg.tool_result);
            console.log(result);
        },
        onWebSocketMessage: msg => console.log(msg, "onWebSocketMessage"),
        onReceivedResponseAudioTranscriptDone: msg => console.log(msg, "onReceivedResponseAudioTranscriptDone"),

        enableInputAudioTranscription: true,
        onReceivedInputAudioBufferSpeechStarted: () => {
            stopAudioPlayer();
        },
        onReceivedResponseAudioTranscriptDelta: message => {
            if (isInturpted) {
                stopAudioPlayer();
                setIsMicMuted(false); // Mute mic when audio starts

                return;
            }

            setConversation(prev => {
                const existingMessageIndex = prev.findIndex(m => m.responseId === message.response_id && !m.isComplete);

                if (existingMessageIndex !== -1) {
                    return prev.map((msg, idx) =>
                        idx === existingMessageIndex
                            ? { ...msg, content: msg.content + message.delta } // Append new text to ongoing message
                            : msg
                    );
                }

                return [
                    ...prev,
                    {
                        role: "Assistant",
                        content: message.delta,
                        responseId: message.response_id,
                        isComplete: false,
                        timestamp: new Date().toISOString().replace("Z", "")
                    }
                ];
            });
        },
        onReceivedResponseDone: message => {
            console.log(message.response.usage, "usage is");

            setConversation(prev => prev.map(msg => (msg.responseId === message.response.id ? { ...msg, isComplete: true } : msg)));
        },
        onReceivedInputAudioTranscriptionCompleted: message => {
            setIsInturpted(false);

            setConversation(prev => [
                ...prev,
                {
                    role: "User",
                    content: message.transcript,
                    isComplete: true,
                    timestamp: new Date().toISOString().replace("Z", "")
                }
            ]);
        }
    });

    const { reset: resetAudioPlayer, play: playAudio, stop: stopAudioPlayer } = useAudioPlayer();
    const { start: startAudioRecording, stop: stopAudioRecording } = useAudioRecorder({
        onAudioRecorded: base64 => {
            if (!isMicMutedRef.current) {
                addUserAudio(base64);
            }
        }
    });

    const onToggleListening = async () => {
        if (!isRecording) {
            setConversation([]);

            startSession();
            await startAudioRecording();
            resetAudioPlayer();
            setIsRecording(true);
            setConversationFinished(false);
        } else {
            await stopAudioRecording();
            stopAudioPlayer();
            inputAudioBufferClear();
            setIsRecording(false);
            setIsMicMuted(false);
            setIsInturpted(true);
            closeSession();
            setConversationFinished(true);
            // After stopping the interview, send the data and show the modal
            // setShowThankYouModal(true);
        }
    };

    const { t } = useTranslation();

    // const triggerToast = () => {
    //     setMessage("Passcode validated successfully");
    // };
    return (
        <div className="flex min-h-screen flex-col bg-gradient-to-br from-blue-100 to-purple-100 text-gray-900">
            <header className="py-6">
                <div className="container mx-auto px-4">
                    <h1 className="bg-gradient-to-r from-indigo-500 to-purple-600 bg-clip-text text-center text-4xl font-extrabold text-transparent">
                        {t("app.title")}
                    </h1>
                </div>
            </header>
            {/* <Toast message={message} /> */}

            <div className="flex flex-grow items-center justify-center">
                <section className="msger">
                    <>
                        <header className="msger-header">
                            <div className="msger-header-title">
                                <i className="fas fa-comment-alt"></i> Conversation
                            </div>
                            <div className="msger-header-options">
                                <span>
                                    <i className="fas fa-cog"></i>
                                </span>
                            </div>
                        </header>
                        {(isRecording || conversation.length > 0) && (
                            <main className={` ${conversation.length > 0 && "msger-chat vh-10"}`}>
                                <ConversationModal messages={conversation} />
                            </main>
                        )}
                        <main className="p-4">
                            <div className={`mb-4 flex items-center justify-center ${conversation.length == 0 && "pt-10"} `}>
                                {!conversationFinished ? (
                                    <>
                                        <Button
                                            onClick={onToggleListening}
                                            className={`btn-bg mb-4 ${isRecording ? "bg-red-500 hover:bg-red-600" : "bg-green-500 hover:bg-green-600"}`}
                                            aria-label={isRecording ? t("app.stopRecording") : t("app.startRecording")}
                                        >
                                            {isRecording ? (
                                                <>
                                                    <MicOff className="mr-2 h-5 w-5" />
                                                    {t("Finish conversation")}
                                                </>
                                            ) : (
                                                <>
                                                    <Mic className="mr-2 h-6 w-6" />
                                                    {t("Start conversation")}
                                                </>
                                            )}
                                        </Button>
                                        {isMicMuted && (
                                            <Button
                                                onClick={() => {
                                                    stopAudioPlayer();
                                                    setIsMicMuted(false);
                                                    setIsInturpted(true);
                                                }}
                                                className="mb-4 ml-5 bg-yellow-500 hover:bg-yellow-600"
                                                aria-label="Interrupt audio"
                                            >
                                                <CircleStop className="mr-2 h-6 w-6" />
                                                Interrupt
                                            </Button>
                                        )}
                                    </>
                                ) : (
                                    <Button
                                        onClick={() => window.location.reload()}
                                        className="btn-bg mb-4 bg-blue-500 hover:bg-blue-600"
                                        aria-label="Refresh page"
                                    >
                                        {t("Refresh")}
                                    </Button>
                                )}
                            </div>

                            <StatusMessage isRecording={isRecording} />
                        </main>
                    </>
                </section>
            </div>
            <footer className="bg-white py-4 shadow-inner">
                <div className="container mx-auto px-4 text-center">
                    <p className="text-sm text-gray-600">{t("app.footer")}</p>
                </div>
            </footer>
        </div>
    );
}

export default App;
